import React, { useCallback, useEffect, useState } from 'react';
import RequestListLayout from 'components/base/request-list-layout';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { Link, useLocation } from 'react-router-dom';
import RequestListFilters from 'components/base/request-list-filters';
import { SelectOption } from 'components/base/multi-select-dropdown';
import { ReactComponent as NoProcurement } from 'assets/icons/no-procurement.svg';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { debounce } from 'utils/functions';
import useDashboardContext from 'contexts/dashboard.context';
import { PrepRequestType } from 'models/prep-request.model';
import { ENUM_REQUEST_STATUS } from 'models/common.model';
import PrepRequestCard from 'components/base/prep-request-card';
import { SocketIOConnection } from 'utils/socket-config';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

export type DateRangeType = {
  startDate: Date;
  endDate: Date;
};

type RequestStatusFilter = ENUM_REQUEST_STATUS;

type PrepRequestFilters = {
  search: string;
  status: RequestStatusFilter[];
  dateRange: DateRangeType | null;
  current_page: number;
};

const KitchenRequestList = () => {
  const [isFetchingProcurements, setIsFetchingProcurements] = useState(false);
  const [procurements, setProcurements] = useState<PrepRequestType[]>([]);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState<PrepRequestFilters>({
    search: '',
    status: [],
    dateRange: null,
    current_page: 1
  });
  const { activeStore } = useDashboardContext();
  const { pathname } = useLocation();
  const { can } = useRoleContext();
  const canWriteKitchenRequisition = can(InventoryModule.KitchenRequisition, RolePermission.Write);

  const initSocket = async () => {
    const conn = await SocketIOConnection.getConnection();
    const roomName = `prep_kitchen_request:${activeStore?.id}`;

    conn.onReconnect(async () => {
      conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
    });

    conn.addEventListener(roomName, 'prep_kitchen_request_updated', (data: PrepRequestType) => {
      setProcurements((prev) => {
        const findRequest = prev?.find((item) => item.id === data.id);
        if (findRequest) {
          return prev?.map((item) => (item.id === data.id ? { ...item, ...data } : item));
        } else {
          return [data, ...prev];
        }
      });
    });
    conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
    return () => {
      conn.removeListener(roomName, 'prep_kitchen_request_updated');
    };
  };
  const removeListener = async () => {
    const conn = await SocketIOConnection.getConnection();
    const roomName = `kitchen_request:${activeStore?.id}`;
    conn.removeListener(roomName, 'prepkitchen_request_updated');
  };
  useEffect(() => {
    if (!activeStore?.id) return;
    initSocket();
    return () => {
      removeListener();
    };
  }, []);
  useEffect(() => {
    if (!activeStore?.id) return;
    initSocket();
    return () => {
      removeListener();
    };
  }, []);

  const normalizeStatusFilter = (values: RequestStatusFilter[]) => {
    if (values.length === 0) {
      return [];
    }
    return values;
  };

  const fetchProcurements = async (
    searchString: string,
    status: RequestStatusFilter[],
    current_page?: number,
    startDate?: Date,
    endDate?: Date
  ) => {
    if (!activeStore?.id) return;

    try {
      setIsFetchingProcurements(true);
      const normalizedStatus = normalizeStatusFilter(status);
      const statusParam = normalizedStatus.length === 1 ? normalizedStatus[0] : normalizedStatus;
      const response = await apiService.get(`prep-kitchen-request/stores/${activeStore.id}`, {
        version: 'v2',
        params: {
          item_type: 'prep',
          store_id: activeStore.id,
          code: searchString,
          status: statusParam,
          page: current_page || '1',
          created_at_after: startDate?.toISOString().split('T')[0] || '',
          created_at_before: endDate?.toISOString().split('T')[0] || ''
        }
      });
      if (response) {
        setProcurements(response?.data?.data);
        setLastPage(Math.ceil(response?.data?.pagination?.total / 10));
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching procurements'}
          type='error'
        />
      );
    } finally {
      setIsFetchingProcurements(false);
    }
  };

  const debounceFetchProcurements = useCallback(debounce(fetchProcurements, 1000), []);

  useEffect(() => {
    setIsFetchingProcurements(true);
    setProcurements([]);
    debounceFetchProcurements(
      filters.search,
      filters.status,
      filters.current_page,
      filters.dateRange?.startDate,
      filters.dateRange?.endDate
    );
  }, [filters, activeStore?.id]);
  const isKitchenRoute = pathname.includes('kitchen');
  const statusOptions: SelectOption<RequestStatusFilter>[] = [
    { text: ENUM_REQUEST_STATUS.REQUESTED, value: ENUM_REQUEST_STATUS.REQUESTED },
    { text: ENUM_REQUEST_STATUS.CANCELLED, value: ENUM_REQUEST_STATUS.CANCELLED },
    { text: ENUM_REQUEST_STATUS.SENT, value: ENUM_REQUEST_STATUS.SENT },
    { text: ENUM_REQUEST_STATUS.RECEIVED, value: ENUM_REQUEST_STATUS.RECEIVED },
    { text: ENUM_REQUEST_STATUS.REVIEWED, value: ENUM_REQUEST_STATUS.REVIEWED }
  ];

  return (
    <RequestListLayout
      title='Manage Request (Prep Mixes)'
      description='Manage inventory item requests'
      actions={
        !isKitchenRoute && canWriteKitchenRequisition ? (
          <Link to={MAIN_STORE_ROUTE_KEYS.NEW_PROCUREMENT}>
            <Button variant='block' color='primary' className='px-6 py-2 text-sm'>
              New Prep Team Request
            </Button>
          </Link>
        ) : null
      }
      filters={
        <RequestListFilters
          statusMode='multi'
          statusOptions={statusOptions}
          filters={filters}
          onFilterChange={setFilters}
          pageCount={lastPage}
        />
      }>
      {isFetchingProcurements ? (
        <div className='pt-10'>
          <ActivityIndicator />
        </div>
      ) : procurements.length === 0 ? (
        <div className='grid place-items-center mt-24'>
          <NoProcurement className='mb-5' />
          <p className='font-medium text-[#5E6164]'>You’re yet to make any procurement</p>
        </div>
      ) : (
        <div className='mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {procurements.map((p, i) => (
            <PrepRequestCard key={i} request={p} />
          ))}
        </div>
      )}
    </RequestListLayout>
  );
};

export default KitchenRequestList;
